import * as angular from 'angular';
import { IHttpRequestConfigHeaders } from 'angular';
import * as _ from 'underscore';
import { HttpService } from '../../../app.common/modules/api/http.service';
import { BlobService } from '../../../app.common/modules/common/services/blob.service';
import { EnumService } from '../../../app.common/modules/common/services/enum.service';
import { AccountService } from '../account/account.service';
import { IPaginationStoresFilter } from '../api';
import { RetailerService } from '../api/retailer.service';
import { OpeningHours, Store, StoreForRulesPagingResult, StoreForShopingListItem, StoreInfoListItem, StoreListItem, StoresPagingResult } from '../core';
import { IPaginationStoreTableResult, IShopBusinessType, IStoreTableShortModel, ITableSettings } from './models';

export class ShopService {
    public static $inject = ['http', 'retailerService', 'blobService', 'enumService', 'accountService'];
    private businessTypes: {[key: number]: IShopBusinessType[]};

    constructor(
        private http: HttpService,
        private retailerService: RetailerService,
        private blobService: BlobService,
        private enumService: EnumService,
        private accountService: AccountService

    ) { this.businessTypes = {}; }

    public getStores = (settings: IPaginationStoresFilter) => {
        return this.http.get<StoreListItem>(this.retailerService.stores(), { params: settings });
    }

    public getStore = (id: number) => {
        return this.http.get<Store>(this.retailerService.store(id));
    }

    public getStoresForRules = () => {
        return this.http.get<StoreForRulesPagingResult>(this.retailerService.storesForRules());
    }

    public getOpeningHours = (id: number) => {
        return this.http.get<OpeningHours>(this.retailerService.openingHours(id));
    }

    public storeExist = (name: string, cb) => {
        return this.http.get<boolean>(this.retailerService.storeExist(name));
    }

    public updateOpeningHours = (id: number, data): angular.IPromise<void> => {
        return this.http.post(this.retailerService.openingHours(id), data);
    }

    public updateStore = (id: number, store) => {
        this.prepareStoreBeforeSaving(store);
        return this.http.put(this.retailerService.store(id), store);
    }

    public archiveStore = (id: number): angular.IPromise<void> => {
        return this.http.post(this.retailerService.archiveStore(id), null);
    }

    public restoreStore = (id: number): angular.IPromise<void> => {
        return this.http.post(this.retailerService.restoreStore(id), null);
    }

    public createStore = (store): angular.IPromise<number> => {
        this.prepareStoreBeforeSaving(store);
        return this.http.post(this.retailerService.store(), store);
    }

    public getStoreCampaigns = (id: number) => {
        return this.http.get<StoresPagingResult>(this.retailerService.storeCampaigns(id));
    }

    public getBusinessTypes = async () => {
        const languageId = this.accountService.userData.languageID;
        if (_.isEmpty(this.businessTypes[languageId])) {
            const types = await this.http.get<[string, number]>(this.retailerService.storeBusinessTypes());
            const regex = new RegExp('\\W+', 'g');
            this.businessTypes[languageId] = [];
            for (const key in types) {
                if (types.hasOwnProperty(key)) {
                    this.businessTypes[languageId].push({
                        name: key,
                        value: types[key] as number,
                        iconClass: key.toLowerCase().replace(regex, '')
                    }
                    );
                }
            }
        }
        return this.businessTypes[languageId];
    }

    public uploadCover = (shopID: number, file): angular.IPromise<number> => {
        if (!file) {
            throw new Error('The file is absent.');
        }
        const fd = new FormData();
        fd.append('FileImage', this.blobService.dataUriToBlob(file.dataUri), file.name);
        return this.http.post(this.retailerService.storeCover(shopID), fd, {
            transformRequest: angular.identity,
            headers: { 'Content-Type': undefined }
        });
    }

    public uploadLogo = (shopID: number, file) => {
        if (!file) {
            throw new Error('The file is absent.');
        }
        const fd = new FormData();
        fd.append('FileImage', this.blobService.dataUriToBlob(file.dataUri), file.name);
        return this.http.post<number>(this.retailerService.storeLogo(shopID), fd, {
            transformRequest: angular.identity,
            headers: { 'Content-Type': undefined }
        });
    }

    public removeLogo = (shopID: number) => {
        return this.http.delete<boolean>(this.retailerService.storeLogo(shopID));
    }

    public removeCover = (shopID: number) => {
        return this.http.delete<boolean>(this.retailerService.storeCover(shopID));
    }

    public getStoresForJispPay = () => {
        return this.http.get<StoreForShopingListItem[]>(this.retailerService.storesForJispPay());
    }

    public getStoresForManager() {
        return this.http.get<StoreForShopingListItem[]>(this.retailerService.storesForManager());
    }

    public isShowInventory(businessType: number) {
        const type = this.enumService.BusinessType;

        return businessType !== type.ShoppingCentre && businessType !== type.Exhibition && businessType !== type.Exhibitor;
    }

    public getEnumBusinessType() {
        return this.enumService.BusinessType;
    }

    public getStoresForFilter() {
        return this.http.get<StoreInfoListItem[]>(this.retailerService.storesForFilter());
    }

    public getCountry(): any {
        return this.http.get(this.retailerService.country()).then((country: any) => {
            return country;
        });
    }

    public getTablesEnabled(shopID: number) {
        return this.http.get<ITableSettings>(this.retailerService.tablesEnabled(shopID));
    }

    public setTablesEnabled(shopID: number, isEnabled: boolean) {
        const model = {
            Value: isEnabled
        };
        return this.http.post(this.retailerService.setTablesEnabled(shopID), model);
    }

    public setTablesMandatory(shopID: number, isMandatory: boolean) {
        const model = {
            Value: isMandatory
        };
        return this.http.post(this.retailerService.setTablesMandatory(shopID), model);
    }

    public getTables(shopID: number) {
        return this.http.get<IPaginationStoreTableResult>(this.retailerService.tables(shopID));
    }

    public addOrUpdateTables(shopID: number, tableModels: IStoreTableShortModel[]) {
        const model = {
            Items: tableModels
        };
        return this.http.post(this.retailerService.tables(shopID), model);
    }

    public deleteTables(shopID: number, tableIDs: number[]) {
        const model = {
            ItemsId: tableIDs
        };
        const headers: IHttpRequestConfigHeaders = {
            'Content-Type': 'application/json'
        };
        return this.http.delete<void>(this.retailerService.tables(shopID), { headers, data: model });
    }

    public isTableExist(shopID: number, tableName: string) {
        return this.http.get<boolean>(this.retailerService.tableExist(shopID, tableName));
    }

    private prepareStoreBeforeSaving = (store) => {
        if (store.AddressLine2 != null && (store.AddressLine2.length === 0 || !store.AddressLine2.trim())) {
            store.AddressLine2 = null;
        }

        if (store.ContactEmail != null && (store.ContactEmail.length === 0 || !store.ContactEmail.trim())) {
            store.ContactEmail = null;
        }

        if (store.ContactPhoneNumber != null && (store.ContactPhoneNumber.length === 0 || !store.ContactPhoneNumber.trim())) {
            store.ContactPhoneNumber = null;
        }

        if (store.WebsiteUrl != null && (store.WebsiteUrl.length === 0 || !store.WebsiteUrl.trim())) {
            store.WebsiteUrl = null;
        }

        if (store.Description != null && (store.Description.length === 0 || !store.Description.trim())) {
            store.Description = null;
        }

        if (store.Description != null && (store.Description.length === 0 || !store.Description.trim())) {
            store.Description = null;
        }

        if (store.type != null) {
            store.BusinessType = store.type.value;
        }
    }
}

angular.module('naut').service('shopService', ShopService);



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/app.retailer/modules/places/shop.service.ts